import {AxiosInstance} from "axios";
import {CustomerCertification} from "@/customer/interfaces/Customer";

interface CreateCertificationNotificationRequest {
    notification_template_id?: number;
    content?: string;
}

/**
 * The customer QR code resource.
 * @author Tony Laurent <t.laurent@apodispharma.com>
 * @param {Object} client - The injected client.
 * @returns {Object} The public object.
 */
export default function (client: AxiosInstance) {
    return {
        /**
         * Read the QR code of a customer.
         *
         * @param {number} customerId - The ID of the customer.
         * @returns {Promise} Promise object represents the HTTP request.
         */
        readQrCode(customerId: number) {
            return client
                .get(`/customers/${customerId}/certification/qrcode`, {
                    responseType: 'blob'
                })
                .then(response => response.data);
        },

        /**
         * Certify a phone number
         *
         * @param {number} customerId - The customer id.
         * @param {string} phone - The phone number.
         *
         * @return {*}
         */
        async certifyPhone(customerId: number, phone: string): Promise<CustomerCertification> {
            return client
                .post(`/customers/${customerId}/certification`, {data: {phone}})
                .then(response => response.data.data);
        },

        /**
         * Certify a phone number for a user.
         *
         * @param {number} customerId - The customer id.
         * @param {number} userId - The user id.
         * @param {string} phone - The phone number.
         *
         * @return {*}
         */
        async certifyUserWithPhone(customerId: number, userId: number, phone: string): Promise<CustomerCertification> {
            return client
                .post(`/customers/${customerId}/user-certification`, {data: {phone, user_id: userId}})
                .then(response => response.data.data);
        },

        /**
         * Cancel a customer certification
         *
         * @param {number} customerId - The customer id.
         *
         * @return {*}
         */
        cancel(customerId: number) {
            return client
                .delete(`/customers/${customerId}/certification`)
                .then(response => response.data.data);
        },

        /**
         * Get the certification pdf url.
         *
         * @param {number} customerId - The customer id.
         * @param {string} type - The onboarding type.
         *
         * @return {Promise<Blob>}
         */
        pdfUrl(customerId: number, type: string) {
            return client.get(`/customers/${customerId}/certification/pdf?type=${type}`, {responseType: 'blob'})
                .then(response => response.data);
        },

        /**
         * Get the certifications for a given customer.
         *
         * @param customerId The customer id.
         * @param status The certification status.
         * One of : 'pending', 'validated', 'pre_certified'
         *
         * @return {Promise<Array>}
         */
        certifications(
            customerId: number,
            status: 'pending' | 'validated' | 'pre_certified' | null = null
        ) {
            return client
                .get(`/customers/${customerId}/certifications`, {params: {status}})
                .then(response => response.data.data);
        },

        /**
         * Create a certification notification.
         */
        async createNotification(certificationId: number, data: CreateCertificationNotificationRequest): Promise<void> {
            return client
                .post(`certifications/${certificationId}/notifications`,
                    {data}
                );
        }
    };
}
